import React, { useCallback, useState } from 'react';
import { FilterSidebar } from '../components/FilterSidebar';
import { FilmCard } from '../components/FilmCard';
import { Button } from '../components/ui/Button';
import { useCatalogFilms, CatalogFilters, DEFAULT_FILTERS } from '../hooks/useFilms';
import { Filter, ChevronDown, Loader2, ChevronLeft, ChevronRight } from 'lucide-react';
export function Catalog() {
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [sortBy, setSortBy] = useState('relevance');
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<CatalogFilters>(DEFAULT_FILTERS);
  const {
    films,
    loading,
    totalCount,
    totalPages
  } = useCatalogFilms(page, filters, sortBy);
  const handleApplyFilters = useCallback((newFilters: CatalogFilters) => {
    setFilters(newFilters);
    setPage(1); // Reset to page 1 when filters change
  }, []);
  const handleSortChange = (newSort: string) => {
    setSortBy(newSort);
    setPage(1);
  };
  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages: (number | '...')[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (page > 3) pages.push('...');
      const start = Math.max(2, page - 1);
      const end = Math.min(totalPages - 1, page + 1);
      for (let i = start; i <= end; i++) pages.push(i);
      if (page < totalPages - 2) pages.push('...');
      pages.push(totalPages);
    }
    return pages;
  };
  const activeFilterCount = filters.genres.length + filters.ageCategories.length + filters.studios.length + (filters.yearFrom > 1920 ? 1 : 0) + (filters.yearTo < 2000 ? 1 : 0) + (filters.search.trim() ? 1 : 0);
  return <div className="min-h-screen bg-[#0a0a0a]" data-id="element-241">
      {/* Page Header */}
      <div className="bg-[#050505] text-white py-16 border-b border-[#222]" data-id="element-242">
        <div className="container mx-auto px-4" data-id="element-243">
          <h1 className="font-noir text-5xl md:text-7xl font-medium mb-6 tracking-tight" data-id="element-244">
            Каталог
          </h1>
          <p className="text-gray-500 max-w-2xl font-serif text-lg font-light leading-relaxed" data-id="element-245">
            Полное собрание картин советского периода. Тени, свет и вечные
            истории.
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12" data-id="element-246">
        <div className="flex flex-col md:flex-row gap-10" data-id="element-247">
          {/* Sidebar */}
          <FilterSidebar isOpen={isFilterOpen} onClose={() => setIsFilterOpen(false)} filters={filters} onApply={handleApplyFilters} className="w-full md:w-72 shrink-0" data-id="element-248" />

          {/* Main Content */}
          <div className="flex-1" data-id="element-249">
            {/* Controls */}
            <div className="flex flex-wrap items-center justify-between gap-4 mb-10 pb-6 border-b border-[#222]" data-id="element-250">
              <div className="flex items-center gap-4" data-id="element-251">
                <span className="font-serif text-sm text-gray-300 uppercase tracking-widest" data-id="element-252">
                  {totalCount} фильмов
                </span>
                <Button variant="outline" size="sm" className="md:hidden ml-2 relative" onClick={() => setIsFilterOpen(true)} data-id="element-253">
                  <Filter className="w-3 h-3 mr-2" data-id="element-254" />
                  Фильтры
                  {activeFilterCount > 0 && <span className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-[#D22B2B] text-white text-[9px] flex items-center justify-center rounded-full" data-id="element-255">
                      {activeFilterCount}
                    </span>}
                </Button>
              </div>

              <div className="flex items-center gap-3" data-id="element-256">
                <span className="text-xs text-gray-600 font-serif uppercase tracking-widest hidden sm:inline" data-id="element-257">
                  Сортировать:
                </span>
                <div className="relative group" data-id="element-258">
                  <select value={sortBy} onChange={e => handleSortChange(e.target.value)} className="appearance-none bg-[#141414] border border-[#333] px-4 py-2 pr-10 text-xs font-serif uppercase tracking-widest text-gray-300 focus:outline-none focus:border-[#D22B2B] cursor-pointer transition-colors hover:border-gray-500" data-id="element-259">
                    <option value="relevance" data-id="element-260">Случайный порядок</option>
                    <option value="year_desc" data-id="element-261">Сначала новые</option>
                    <option value="year_asc" data-id="element-262">Сначала старые</option>
                    <option value="title" data-id="element-263">По названию</option>
                    <option value="rating" data-id="element-264">По рейтингу</option>
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-500 pointer-events-none group-hover:text-[#D22B2B] transition-colors" data-id="element-265" />
                </div>
              </div>
            </div>

            {/* Active Filters Display */}
            {activeFilterCount > 0 && <div className="flex flex-wrap items-center gap-2 mb-8" data-id="element-266">
                <span className="text-[10px] font-serif uppercase tracking-widest text-gray-500 mr-2" data-id="element-267">
                  Фильтры:
                </span>
                {filters.search.trim() && <span className="inline-flex items-center gap-1 px-2 py-1 bg-[#D22B2B]/10 border border-[#D22B2B]/30 text-[10px] text-[#D22B2B] uppercase tracking-wider" data-id="element-268">
                    «{filters.search}»
                  </span>}
                {(filters.yearFrom > 1920 || filters.yearTo < 2000) && <span className="inline-flex items-center gap-1 px-2 py-1 bg-[#D22B2B]/10 border border-[#D22B2B]/30 text-[10px] text-[#D22B2B] uppercase tracking-wider" data-id="element-269">
                    {filters.yearFrom}–{filters.yearTo}
                  </span>}
                {filters.genres.map(g => <span key={g} className="inline-flex items-center gap-1 px-2 py-1 bg-[#D22B2B]/10 border border-[#D22B2B]/30 text-[10px] text-[#D22B2B] uppercase tracking-wider capitalize" data-id="element-270">
                    {g}
                  </span>)}
                {filters.ageCategories.map(a => <span key={a} className="inline-flex items-center gap-1 px-2 py-1 bg-[#D22B2B]/10 border border-[#D22B2B]/30 text-[10px] text-[#D22B2B] uppercase tracking-wider" data-id="element-271">
                    {a}
                  </span>)}
                {filters.studios.map(s => <span key={s} className="inline-flex items-center gap-1 px-2 py-1 bg-[#D22B2B]/10 border border-[#D22B2B]/30 text-[10px] text-[#D22B2B] uppercase tracking-wider" data-id="element-272">
                    {s}
                  </span>)}
              </div>}

            {/* Grid */}
            {loading ? <div className="flex flex-col items-center justify-center py-32" data-id="element-273">
                <Loader2 className="w-10 h-10 text-[#D22B2B] animate-spin mb-4" data-id="element-274" />
                <p className="text-gray-500 font-serif text-sm uppercase tracking-widest" data-id="element-275">
                  Загрузка каталога...
                </p>
              </div> : films.length === 0 ? <div className="flex flex-col items-center justify-center py-32 border border-[#222] bg-[#141414]/50" data-id="element-276">
                <p className="font-noir text-3xl text-white mb-3" data-id="element-277">
                  Ничего не найдено
                </p>
                <p className="text-gray-500 font-serif text-sm mb-8" data-id="element-278">
                  Попробуйте изменить параметры фильтрации
                </p>
                <Button variant="outline" onClick={() => handleApplyFilters(DEFAULT_FILTERS)} className="border-[#333] text-gray-300" data-id="element-279">
                  Сбросить фильтры
                </Button>
              </div> : <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8" data-id="element-280">
                {films.map((film, idx) => <FilmCard key={film.id} film={film} index={idx} data-id="element-281" />)}
              </div>}

            {/* Pagination */}
            {!loading && totalPages > 1 && <div className="mt-16 flex flex-wrap items-center justify-center gap-2" data-id="element-282">
                <Button variant="outline" disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))} className="border-[#333] text-gray-400 hover:text-white disabled:opacity-30" data-id="element-283">
                  <ChevronLeft className="w-4 h-4 mr-1" data-id="element-284" />
                  Назад
                </Button>

                {getPageNumbers().map((p, i) => p === '...' ? <span key={`dots-${i}`} className="flex items-center px-3 text-gray-600" data-id="element-285">
                      …
                    </span> : <Button key={p} variant={p === page ? 'primary' : 'outline'} onClick={() => setPage(p)} className={p === page ? 'bg-[#D22B2B] border-[#D22B2B]' : 'border-[#333] text-gray-300 hover:text-white'} data-id="element-286">
                      {p}
                    </Button>)}

                <Button variant="outline" disabled={page >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))} className="border-[#333] text-gray-400 hover:text-white disabled:opacity-30" data-id="element-287">
                  Вперед
                  <ChevronRight className="w-4 h-4 ml-1" data-id="element-288" />
                </Button>

                {/* Page info */}
                <div className="w-full text-center mt-4" data-id="element-289">
                  <span className="text-[10px] font-serif uppercase tracking-widest text-gray-600" data-id="element-290">
                    Страница {page} из {totalPages} · {totalCount} фильмов
                  </span>
                </div>
              </div>}
          </div>
        </div>
      </div>
    </div>;
}